import {makeAutoObservable} from "mobx";
import AuthService from "../services/AuthService";
import { buildDemoUserMenu } from "../demo/applyDemoMenuOverrides";

// Demo: мок даних + розширення меню садочками з GET /api/kindergarten/kindergartens (лише demo access group).
// We detect demo users by Access Group ID (created in DB): Demo_Osvita => 3870019203884910202
const DEMO_ACCESS_GROUP_ID = "3870019203884910202";

const getUserAccessGroupId = (userInfo = {}) => {
    // Backend payload shape may vary. Try common fields.
    const raw =
        userInfo?.access_group_id ??
        userInfo?.accessGroupId ??
        // access_group is menu array in our auth payload; only treat it as id if it's not an array
        (Array.isArray(userInfo?.access_group) ? null : userInfo?.access_group) ??
        userInfo?.accessGroup ??
        userInfo?.access_group_fk ??
        null;

    // Normalize to string for safe comparison with bigints
    return raw === null || raw === undefined ? null : String(raw);
}

const isDemoUser = (userInfo = {}) => {
    const groupId = getUserAccessGroupId(userInfo);
    return groupId === DEMO_ACCESS_GROUP_ID;
}

export default class Store {
    user = {}
    isLoading = true
    isAuth = false
    isError = false
    errorMessage = ""
    isDemo = false

    constructor() {
        makeAutoObservable(this)
    }

    setAuth(boolean) {
        this.isAuth = boolean
    }

    setLoading(boolean) {
        this.isLoading = boolean
    }

    setError(boolean, message = "") {
        this.isError = boolean
        this.errorMessage = message
    }

    setUser(userInfo) {
        this.user = userInfo
    }

    setDemo(boolean) {
        this.isDemo = boolean
        try {
            if (boolean) localStorage.setItem("isDemo", "1")
            else localStorage.removeItem("isDemo")
        } catch (e) {
        }
    }

    initDemoState() {
        try {
            this.isDemo = localStorage.getItem("isDemo") === "1"
        } catch (e) {
            this.isDemo = false
        }
    }

    logOff() {
        this.isAuth = false
        this.user = {}
    }

    async login(username, password) {
        this.setLoading(true)
        this.setError(false)
        try {
            const response = await AuthService.login(username, password)
            this.setAuth(true)
            this.setUser(response.data)

            // Enable demo mode only for demo access group
            const demo = isDemoUser(response.data)
            this.setDemo(demo)
            if (demo) {
                this.setUser(await buildDemoUserMenu(response.data))
            }
            if (import.meta?.env?.DEV) {
                // eslint-disable-next-line no-console
                console.info("[DEMO]", { enabled: demo, access_group_id: response?.data?.access_group_id })
            }
        } catch (error) {
            this.setAuth(false)
            this.setError(true, `${error?.response?.data?.message ? error.response.data.message : 'Сервер тимчасово недоступний'}`)
        } finally {
            this.setLoading(false)
        }
    }

    async logout() {
        this.setLoading(true)
        try {
            if (!this.isDemo) {
                await AuthService.logout()
            }
        } catch (error) {
        } finally {
            this.setDemo(false)
            this.setUser({})
            this.setAuth(false)
            this.setLoading(false)
        }
    }

    async checkAuth() {
        try {
            const response = await AuthService.checkAuth()
            this.setAuth(true)
            this.setUser(response.data)

            // Recompute demo mode on page refresh based on user's access group
            const demo = isDemoUser(response.data)
            this.setDemo(demo)
            if (demo) {
                this.setUser(await buildDemoUserMenu(response.data))
            }
            if (import.meta?.env?.DEV) {
                // eslint-disable-next-line no-console
                console.info("[DEMO]", { enabled: demo, access_group_id: response?.data?.access_group_id })
            }
        } catch (error) {
        } finally {
            this.setLoading(false)
        }
    }
}